import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow, TableSortLabel, CircularProgress, IconButton, Tooltip, Switch, Popover, Typography, Divider } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Add, Edit, Delete, Star } from '@mui/icons-material';
import { adminService } from '../../../shared/services/adminService';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { Snackbar, Alert, Box, LinearProgress } from '@mui/material';
import { Loader } from '../../../shared/components/Loader';

interface Admin {
    _id: string;
    name: string;
    email: string;
    role?: string;
    is_active?: boolean;
}

export const Admin: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin, currentAdmin } = usePermission();
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [allAdmins, setAllAdmins] = useState<Admin[]>([]);
    const [pageSize, setPageSize] = useState(10);
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);



    const getAdminList = useCallback(async () => {
        setIsLoading(true);
        const payload: any = {
            page: 1,
            limit: 1000
        };

        try {
            const response = await adminService.list(payload);

            const outerData = response?.data || response;
            const fetchedItems = outerData?.data || outerData?.items || (Array.isArray(outerData) ? outerData : []);

            setAllAdmins(Array.isArray(fetchedItems) ? fetchedItems : []);
        } catch (error: any) {
            console.error('Error fetching admin list:', error);
            setSnackbar({ open: true, message: 'Error loading admin list' });
            setAllAdmins([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        getAdminList();
    }, [getAdminList]);

    // Client-side filtering
    const filteredAdmins = React.useMemo(() => {
        let activeRole = currentAdmin?.role;
        if (!activeRole) {
            const adminDetail = localStorage.getItem('userDetail');
            if (adminDetail) {
                try {
                    const parsed = JSON.parse(adminDetail);
                    activeRole = parsed.role || parsed.roleType;
                } catch (e) { }
            }
        }

        let adminList = allAdmins.map((a: any) => ({
            ...a,
            _id: a._id ?? a.id ?? a.adminId,
            is_active: a.is_active ?? a.isActive ?? true,
            role: a.role || a.roleType || 'admin'
        }));

        // Only filter if we are NOT a super_admin
        if (activeRole && activeRole !== 'super_admin') {
            adminList = adminList.filter((i: Admin) => (i.role || '').toLowerCase() === 'admin');
        }

        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            adminList = adminList.filter(admin =>
                (admin.name?.toLowerCase() || '').includes(q) ||
                (admin.email?.toLowerCase() || '').includes(q) ||
                (admin.role?.toLowerCase() || '').includes(q)
            );
        }

        if (sortData?.active) {
            adminList.sort((a, b) => {
                const aValue = (a as any)[sortData.active];
                const bValue = (b as any)[sortData.active];

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortData.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }
                if (aValue < bValue) return sortData.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortData.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return adminList;
    }, [allAdmins, searchInput, sortData, currentAdmin?.role]);

    // Client-side pagination
    const paginatedAdmins = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredAdmins.slice(start, start + pageSize);
    }, [filteredAdmins, currentPage, pageSize]);


    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handleSort = (property: string) => {
        const isAsc = sortData?.active === property && sortData?.direction === 'asc';
        setSortData({
            active: property,
            direction: isAsc ? 'desc' : 'asc'
        });
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleStatusChange = async (admin: Admin) => {
        if (isSuperAdmin() && admin.role !== 'super_admin') {
            try {
                const response = await adminService.updateStatus(admin._id, { is_active: !admin.is_active });
                setSnackbar({ open: true, message: response.message || 'Status updated' });
                getAdminList(); // Re-fetch all data to update the list
            } catch (error) {
                setSnackbar({ open: true, message: 'Error updating status' });
            }
        }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedAdminId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedAdminId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedAdminId) return;

        try {
            await adminService.delete(selectedAdminId);
            setSnackbar({ open: true, message: 'Admin deleted successfully' });
            getAdminList();
        } catch (error) {
            setSnackbar({ open: true, message: 'Error deleting admin' });
        } finally {
            handleDeleteClose();
        }
    };

    const handleEdit = (admin: Admin) => {
        navigate(`/admin/edit/${admin._id}`);
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Admin
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        onSearch={() => setCurrentPage(0)} // Trigger re-render of filteredAdmins
                        placeholder="Search"
                        className="w-full sm:w-64"
                    />
                    {(isSuperAdmin() || hasPermission('admin.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<Add />}
                            onClick={() => navigate('/admin/add')}
                            sx={{ px: 3, py: 1.5, borderRadius: '6px' }}
                        >
                            Add Admin
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full flex-1">
                    {isLoading && allAdmins.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && allAdmins.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">
                                        <TableSortLabel
                                            active={sortData?.active === 'name'}
                                            direction={sortData?.active === 'name' ? sortData.direction : 'asc'}
                                            onClick={() => handleSort('name')}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">
                                        <TableSortLabel
                                            active={sortData?.active === 'email'}
                                            direction={sortData?.active === 'email' ? sortData.direction : 'asc'}
                                            onClick={() => handleSort('email')}
                                        >
                                            Email
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">
                                        <TableSortLabel
                                            active={sortData?.active === 'is_active'}
                                            direction={sortData?.active === 'is_active' ? sortData.direction : 'asc'}
                                            onClick={() => handleSort('is_active')}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredAdmins.length === 0}
                            emptyColSpan={5}
                        >
                            {paginatedAdmins.map((item, i) => (
                                <TableRow
                                    key={item._id}
                                    className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                    <TableCell className="!text-gray-700">{(currentPage * pageSize) + i + 1}</TableCell>
                                    <TableCell className="!text-gray-900">
                                        <div className="flex items-center">
                                            {item.name}
                                            {item.role === 'super_admin' && (
                                                <Tooltip title="Super Admin">
                                                    <Star className="!w-4 !h-4 ml-2 text-purple-600" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="!text-gray-700">{item.email}</TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={item.is_active}
                                            onChange={() => handleStatusChange(item)}
                                            disabled={currentAdmin.role !== 'super_admin' || item.role === 'super_admin' || isLoading}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {(isSuperAdmin() || hasPermission('admin.update') || currentAdmin?._id === item._id) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        onClick={() => handleEdit(item)}
                                                        size="small"
                                                        className="text-blue-600 hover:bg-blue-50"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() && item.role !== 'super_admin') && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={(e) => handleDeleteClick(e, item._id)}
                                                        size="small"
                                                        className="text-red-600 hover:bg-red-50"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredAdmins.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(0);
                    }}
                />
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This admin user will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity="info">{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};
